import { SettingsManager } from "@viamap/viamap2-common";
import { useEffect } from "react";
import { ESInterface } from "src/compentsModus/ESInterface";
import { MapitUtils } from "src/managers/MapitUtils";

export type statPrefix =
   "App"
   | "Error"
   | "Window"
   | "FeatureLayer"
   | "ModusPane"
   | "Project"
   | "Search"
   | "MapInteraction"
   | "DataImport" // Importing data into the system 
   | "DataExport" // Exporting data out of the system
   | "DataReport" // Generating reports from data
   | ""
   ;

export class StatTracker {
   private static active: { [key: string]: Date } = {};
   private static hidden: { [key: string]: Date } = {};
   private static closed: { [key: string]: number } = {};
   private static counts: { [key: string]: number } = {};
   private static uuid: string = MapitUtils.getNewUuid();
   private static startTime: Date = new Date();
   private static cognitoID: string = "";
   private static UserID: string = "";
   private static CustomerID: string = "";
   private static isRunning: boolean = false;
   private static debugMode: boolean = false;
   private static _func: () => void;
   private static ESInterface: ESInterface | null = null;

   public static start() {
      if (!StatTracker.isRunning) {
         StatTracker._func = () => {
            if (document.hidden) {
               StatTracker.hide();
               StatTracker.sendStats();
            } else {
               StatTracker.unhide();
            }
         };
         addEventListener('visibilitychange', StatTracker._func);
         StatTracker.isRunning = true;
      }
   }

   public static attachUserInfo(cognitoID: string, userID: string, customerID: string, userSession?: any) {
      (StatTracker as any).cognitoID = cognitoID;
      (StatTracker as any).UserID = userID;
      (StatTracker as any).CustomerID = customerID;
      let esIndex = SettingsManager.getSystemSetting("statTrackerIndex", null, true) || "";
      (StatTracker as any).ESInterface = new ESInterface(esIndex, userSession);
   }

   public static attachDebugger() {
      StatTracker.debugMode = true;
   }

   public static reset() {
      StatTracker.uuid = MapitUtils.getNewUuid();
      StatTracker.startTime = new Date();
      StatTracker.active = {};
      StatTracker.hidden = {};
      StatTracker.closed = {};
      if (StatTracker._func) {
         removeEventListener('visibilitychange', StatTracker._func);
      }
      StatTracker.start();
   }

   public static open(prefix: statPrefix, ...tags: string[]) {
      tags.forEach(tag => {
         let tagIndex = `t_${prefix}_${tag}`;
         if (!prefix)
            tagIndex = `t_${tag}`;
         if (!StatTracker.active[tagIndex]) {
            StatTracker.active[tagIndex] = new Date();
         }
      });
   }

   public static count(prefix: statPrefix, ...tags: string[]) {
      tags.forEach(tag => {
         let tagIndex = `c_${prefix}_${tag}`;
         if (!prefix)
            tagIndex = `c_${tag}`;
         StatTracker.counts[`c_${prefix}_${tag}`] = (StatTracker.counts[`c_${prefix}_${tag}`] || 0) + 1;
      });
   }

   public static close(prefix: statPrefix, ...tags: string[]) {
      tags.forEach(tag => {
         let tagIndex = `t_${prefix}_${tag}`;
         if (!prefix)
            tagIndex = `t_${tag}`;

         if (StatTracker.active[tagIndex]) {
            const duration = Math.round((new Date().getTime() - StatTracker.active[tagIndex].getTime()) / 1000);
            if (StatTracker.debugMode) {
               console.log(`StatTracker: ${tag} closed after ${duration} seconds`);
            }
            StatTracker.closed[tagIndex] = (StatTracker.closed[tagIndex] || 0) + duration
            delete StatTracker.active[tagIndex];
         }
      });
   }

   private static hide() {
      StatTracker.hidden = structuredClone(StatTracker.active);
      Object.keys(StatTracker.active).forEach(tag => {
         let tagName = tag.replace("t_", "");
         StatTracker.close("", tagName);
      });
   }

   private static unhide() {
      Object.keys(StatTracker.hidden).forEach(tag => {
         let tagName = tag.replace("t_", "");
         StatTracker.open("", tagName);
      });
      StatTracker.hidden = {};
   }

   private static sendStats() {
      const stats = JSON.stringify({
         uuid: StatTracker.uuid,
         cognitoID: StatTracker.cognitoID || "Unknown",
         UserID: StatTracker.UserID || "Unknown",
         CustomerID: StatTracker.CustomerID || "Unknown",
         startTime: StatTracker.startTime,
         endTime: new Date(),
         ...StatTracker.closed,
         ...StatTracker.counts
      });
      if (StatTracker.debugMode) console.log('Sending stats:', stats);
      if (StatTracker.ESInterface) {
         StatTracker.ESInterface.updateDocument(StatTracker.uuid, stats).then(() => {
            console.log('Stats sent successfully');
         }).catch((error) => {
            console.error('No Response from ESInterface, Request might have failed', error);
         });
      }
   }
}

export function useStatTracker(prefix: statPrefix, ...tags: string[]) {
   useEffect(() => {
      StatTracker.start();
      StatTracker.open(prefix, ...tags);
      return () => {
         StatTracker.close(prefix, ...tags);
      };
   }, []);
}